import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { ProductTourChatControls } from './types.js';

interface ProductTourContextValue {
  registerChatControls: (controls: ProductTourChatControls | null) => void;
  chatControls: ProductTourChatControls | null;
  /** Bumped to force-show tour (replay). */
  forceReplayToken: number;
  requestReplay: () => void;
}

const ProductTourContext = createContext<ProductTourContextValue | null>(null);

export function ProductTourProvider({ children }: { children: ReactNode }) {
  const [chatControls, setChatControls] = useState<ProductTourChatControls | null>(
    null,
  );
  const [forceReplayToken, setForceReplayToken] = useState(0);
  const controlsRef = useRef<ProductTourChatControls | null>(null);

  const registerChatControls = useCallback((controls: ProductTourChatControls | null) => {
    controlsRef.current = controls;
    setChatControls(controls);
  }, []);

  const requestReplay = useCallback(() => {
    setForceReplayToken((n) => n + 1);
  }, []);

  const value = useMemo(
    () => ({
      registerChatControls,
      chatControls,
      forceReplayToken,
      requestReplay,
    }),
    [registerChatControls, chatControls, forceReplayToken, requestReplay],
  );

  return (
    <ProductTourContext.Provider value={value}>{children}</ProductTourContext.Provider>
  );
}

export function useProductTour(): ProductTourContextValue {
  const ctx = useContext(ProductTourContext);
  if (!ctx) {
    throw new Error('useProductTour must be used within ProductTourProvider');
  }
  return ctx;
}

/** Optional: null outside provider (e.g. login page). */
export function useProductTourOptional(): ProductTourContextValue | null {
  return useContext(ProductTourContext);
}
